import React, { useEffect, useRef, useState } from 'react';
import { useMessage } from '../../hooks/useMessage';
import { FormsProps, FormsResultsDTO } from '../../types/forms';
import { deleteFormResponse, getFormsRows } from '../../api/forms.api';
import Loading from '../loading/Loading';
import { Dialog, DialogContent, Typography } from '@mui/material';
import FormsResultsTable from '../FormsResultsTable/FormsResultsTable';
import { transformFormResponses } from '../../utils/forms.utils';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import DataUpdateForm from './DataUpdateForm';

const ViewAndEditTableForm = ({ formId, linkedFormId, formAllFields, formType, skin }: FormsProps) => {
    const { showMessage, MessageContainer } = useMessage();
    const [ results, setResults ] = useState<FormsResultsDTO['responses']>([]);
    const [ resultsLoading, setResultsLoading ] = useState(true);
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const [ removeLoading, setRemoveLoading ] = useState(false);
    const [ editModalOpen, setEditModalOpen ] = useState(false);
    const [ editingResponseId, setEditingResponseId ] = useState('');
    const responseIdsToRemove = useRef<(string | number)[]>([]);

    let realFormId = +formId;
    if (linkedFormId) {
        realFormId = linkedFormId;
    }

    useEffect(() => {
        setResultsLoading(true);

        getFormsRows(realFormId)
        .then(response => {
            setResults(response.responses);
        })
        .finally(() => setResultsLoading(false));
    }, [ realFormId ]);

    const onBulkDelete = async (responseIdsToRemove: (string | number)[]) => {
        if (responseIdsToRemove.length === 0 || !realFormId) {
            return;
        }

        setRemoveLoading(true);
        Promise.all(
            responseIdsToRemove.map((id) => deleteFormResponse(realFormId, id)),
        )
               .then(() => {
                   showMessage('The response(s) has been deleted.', 'success');
                   const filteredResults = results.filter(item => !responseIdsToRemove.includes(item.response_id));
                   setResults(filteredResults);
               })
               .catch(() => {
                   showMessage('Couldn\'t remove the record(s). Please try again later.', 'error');
               })
               .finally(() => {
                   setConfirmModalOpen(false);
                   setRemoveLoading(false);
               });
    };

    const onDeleteConfirm = async () => {
        if (responseIdsToRemove.current.length === 0 || !realFormId) {
            return;
        }

        setRemoveLoading(true);
        Promise.all(
            responseIdsToRemove.current.map((id) => deleteFormResponse(realFormId, id)),
        )
               .then(() => {
                   showMessage('The response(s) has been deleted.', 'success');
                   const filteredResults = results.filter(item => !responseIdsToRemove.current.includes(item.response_id));
                   setResults(filteredResults);
                   responseIdsToRemove.current = [];
               })
               .catch(() => {
                   showMessage('Couldn\'t remove the record(s). Please try again later.', 'error');
               })
               .finally(() => {
                   setConfirmModalOpen(false);
                   setRemoveLoading(false);
               });

    };

    const onResponseEdit = (responseId: string | number) => {
        setEditingResponseId(responseId + '');
        setEditModalOpen(true);
    };

    const onAfterUpdate = (updatedResult: any) => {
        setResults(results.map(result => {
            if (+result.response_id === updatedResult.response_id) {
                return {
                    ...result,
                    response: updatedResult.data,
                };
            }

            return result;
        }));
    };

    const onDeleteFormResponse = (responseId: string | number) => {
        setConfirmModalOpen(true);
        responseIdsToRemove.current = [ responseId ];
    };

    return (
        <div>
            {resultsLoading ? <Loading/> : (
                <div style={{ margin: '16px', width: '98%' }}>
                    <div>
                        <Typography variant="h4" gutterBottom>Submitted Results</Typography>
                    </div>

                    {formAllFields.length > 0 ? (
                        <FormsResultsTable
                            config={{ fields: formAllFields.filter(field => !field.hidden) }}
                            data={transformFormResponses(results)}
                            onEdit={onResponseEdit}
                            onDelete={onDeleteFormResponse}
                            onDeleteSelected={onBulkDelete}
                            isRemoveLoading={removeLoading}
                            hasActions
                        />
                    ) : (
                        <Typography variant="h6">
                            Couldn't load the results.
                        </Typography>
                    )}
                </div>
            )}
            <ConfirmModal
                isLoading={removeLoading}
                open={confirmModalOpen}
                title="Are you sure?"
                message="You will not be able to restore the removed data!"
                onConfirm={onDeleteConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />
            <MessageContainer/>
            {editModalOpen && editingResponseId && (
                <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
                    <DialogContent style={{ minWidth: 300 }}>
                        <DataUpdateForm
                            formId={realFormId + ''}
                            formType={formType}
                            formAllFields={formAllFields}
                            skin={skin}
                            responseId={editingResponseId}
                            onCancel={() => setEditModalOpen(false)}
                            onAfterUpdate={onAfterUpdate}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default ViewAndEditTableForm;